import getNextUrlObject from '@modules/utilities/utils/getNextUrlObject';
import NextLink from 'next/link';
import { Link, Button, CardActionArea, Tab } from '@rbx/ui';
import React, { ComponentPropsWithoutRef, Ref } from 'react';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { isAbsolutePath } from '@modules/utilities/utils/path';

type ComponentWithDynamicRouting = typeof Link | typeof Button;

const withDynamicRouting = (DisplayComponent: ComponentWithDynamicRouting) => {
  // include fine-grained control over how client-side navigation works
  // between in-app pages, especially across our dynamic route segments
  // e.g., ensures '/en-us' goes to a Landing page, while '/studio' does not
  // using next/link. ref:
  // https://nextjs.org/docs/pages/api-reference/components/link#with-url-object

  return React.forwardRef(function DynamicallyRoutedComponent(
    props: { href: string } & ComponentPropsWithoutRef<typeof DisplayComponent>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: Ref<any>,
  ) {
    const { href, ...restProps } = props;

    const { isLocalePrefixed, urlLocale } = useCurrentLocale();

    if (!href) {
      if (process.env.isExport) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <React.Fragment />;
      }
      throw new Error(`href is required for DynamicallyRouted components. Got ${href}`);
    }

    // next/link cannot handle hash links
    if (href.startsWith('#') || isAbsolutePath(href))
      return <DisplayComponent ref={ref} {...props} />;

    // passHref needed with styled components.
    // legacyBehavior=true is required with passHref.
    // ref: https://nextjs.org/docs/pages/api-reference/components/link#if-the-child-is-a-custom-component-that-wraps-an-a-tag
    return (
      // eslint-disable-next-line react/forbid-elements
      <NextLink
        ref={ref}
        href={getNextUrlObject(href, isLocalePrefixed, urlLocale)}
        passHref
        legacyBehavior>
        <DisplayComponent {...restProps} />
      </NextLink>
    );
  });
};

const DynamicallyRouted = {
  Button: withDynamicRouting(Button),
  Link: withDynamicRouting(Link),
  CardActionArea: withDynamicRouting(CardActionArea),
  Tab: withDynamicRouting(Tab),
};

export default DynamicallyRouted;
